import { Entypo } from "@expo/vector-icons";
import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Chevron from "components/Chevron";
import InnerLoading from "components/InnerLoading";
import Spacer from "components/Spacer";
import USAStatesList from "constants/USAStatesList.json";
import { setSelectedEvent } from "features/slices/reservationFlowSlice";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useFetchTenantBrandingQuery } from "queries/tenant.queries";
import {
  EventSearchDetails,
  getTflEventAvailabilityRequest,
  SearchEventParams,
  searchEventsRequest,
  useAutoCompleteResultQuery,
} from "queries/ticket.queries";
import { Pageable } from "queries/types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Pressable, View } from "react-native";
import { useDebounce } from "use-debounce";
import { toTflSeatSelection } from "utils/navigationUtils";
import Autocomplete, { AutocompleteRef } from "./components/Autocomplete";
import AutoCompleteResult from "./components/AutoCompleteResult";
import EventList from "./components/EventList";
import Pagination from "./components/Pagination";

type CachedResult = {
  key: string;
  value: Pageable<EventSearchDetails>;
};

const getCodeByState = (state: string) => {
  return USAStatesList.find((item) => item.name === state)?.abbreviation;
};

const TflEventSearch = () => {
  const dispatch = useAppDispatch();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const autocompleteRef = useRef<AutocompleteRef>(null);
  // Using as a stack
  const [pastSearchResults, setPastSearchResults] = useState<CachedResult[]>(
    []
  );

  const { data: tenantBranding } = useFetchTenantBrandingQuery();
  const { data: autoCompleteResult, isFetching: autoCompleteFetching } =
    useAutoCompleteResultQuery(debouncedSearchQuery);

  const { mutateAsync: searchEvents, isLoading: searchingEvents } =
    useMutation(searchEventsRequest);

  const {
    mutateAsync: getTflEventAvailability,
    isLoading: getTflEventAvailabilityLoading,
  } = useMutation(getTflEventAvailabilityRequest);

  const renderSearchIcon = () => (
    <Entypo name="magnifying-glass" size={20} color="#8F9BB3" />
  );

  const renderClearIcon = () => (
    <Entypo
      name="cross"
      size={20}
      color="#8F9BB3"
      onPress={() => {
        setSearchQuery("");
        autocompleteRef.current?.clearList();
        autocompleteRef.current?.focus();
      }}
    />
  );

  const getCurrentLocation = async () => {
    try {
      if (!navigator.geolocation) {
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          // Use reverse geocoding service to get postal code
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${position.coords.latitude}&lon=${position.coords.longitude}`
          );
          const data = await response.json();
          if (data.address) {
            const { city, state, town, village } = data.address;
            doSearchEvents({
              city: city || town || village,
              regionCode: getCodeByState(state),
            });
          } else {
            doSearchEvents();
          }
        },
        () => {
          doSearchEvents();
        },
        {
          enableHighAccuracy: false,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } catch (error) {
      doSearchEvents();
    }
  };

  const doSearchEvents = async (params?: SearchEventParams) => {
    const finalParams = {
      page: params?.page || currentPage - 1,
      size: params?.size || 10,
      ...params,
    };
    const events = await searchEvents(finalParams);
    setPastSearchResults((prev) => {
      return [
        ...prev,
        {
          key: JSON.stringify(finalParams),
          value: events,
        },
      ];
    });
  };

  // Handle page change
  const handlePageChange = async (page: number) => {
    setCurrentPage(page);
    doSearchEvents({
      ...JSON.parse(pastSearchResults[pastSearchResults.length - 1].key),
      page: page - 1,
    });
  };

  const handleEventPress = async (event: EventSearchDetails | undefined) => {
    if (!event) {
      return;
    }

    autocompleteRef.current?.hide();
    const eventAvailability = await getTflEventAvailability(event.eventId);
    dispatch(setSelectedEvent(eventAvailability));
    toTflSeatSelection();
  };

  // Check if the performer is already in the past search results
  const handlePerformerPress = (performerName: string) => {
    const lastParam = pastSearchResults[pastSearchResults.length - 1];
    const queryParams = JSON.parse(lastParam.key);
    if (queryParams.performer === performerName) {
      return;
    }
    autocompleteRef.current?.hide();
    doSearchEvents({
      page: 0,
      performer: performerName,
    });
    setCurrentPage(1);
  };

  // Check if the venue is already in the past search results
  const handleVenuePress = (venue: string) => {
    const lastParam = pastSearchResults[pastSearchResults.length - 1];
    const queryParams = JSON.parse(lastParam.key);
    if (queryParams.venue === venue) {
      return;
    }
    autocompleteRef.current?.hide();
    doSearchEvents({
      page: 0,
      venue,
    });
    setCurrentPage(1);
  };

  // Check if the category is already in the past search results
  const handleCategoryPress = (categoryName: string) => {
    const lastParam = pastSearchResults[pastSearchResults.length - 1];
    const queryParams = JSON.parse(lastParam.key);
    if (queryParams.category === categoryName) {
      return;
    }
    doSearchEvents({
      page: 0,
      category: categoryName,
    });
    setCurrentPage(1);
    autocompleteRef.current?.hide();
  };

  const getHeaderByParams = useMemo(() => {
    const params = pastSearchResults[pastSearchResults.length - 1];
    const defaultHeader = "Popular Events Near You";
    if (!params) {
      return defaultHeader;
    }
    const queryParams = JSON.parse(params.key);
    if (queryParams?.category) {
      return `Events in ${queryParams.category}`;
    }
    if (queryParams?.venue) {
      return `Events at ${queryParams.venue}`;
    }
    if (queryParams?.performer) {
      return `Events by ${queryParams.performer}`;
    }
    return defaultHeader;
  }, [pastSearchResults]);

  const cachedResult = pastSearchResults[pastSearchResults.length - 1];

  // When go back is clicked, remove the last search result and restore the page number
  const handleGoBack = () => {
    const newPastSearchResults = [...pastSearchResults];
    newPastSearchResults.pop();
    setPastSearchResults(newPastSearchResults);

    // Restore page number
    const page = JSON.parse(
      newPastSearchResults[newPastSearchResults.length - 1].key
    ).page;
    setCurrentPage(page + 1);
  };

  useEffect(() => {
    getCurrentLocation();
  }, []);

  return (
    <View>
      <Modal
        transparent={true}
        animationType="none"
        visible={getTflEventAvailabilityLoading}
      >
        <InnerLoading />
      </Modal>
      <View
        style={{ backgroundColor: tenantBranding?.primaryColor }}
        className="px-4 pt-4 pb-2 border-b border-[#EDF1F7] rounded-lg z-50"
      >
        <View className="mb-2">
          <Text category="h5" status="control">
            Event Search
          </Text>
        </View>
        <View className="mb-2">
          <Autocomplete
            placeholder="Search for events, teams or performers..."
            value={searchQuery}
            onChangeText={setSearchQuery}
            accessoryRight={searchQuery ? renderClearIcon : renderSearchIcon}
            ref={autocompleteRef}
          >
            <AutoCompleteResult
              autoCompleteFetching={autoCompleteFetching}
              autoCompleteResult={autoCompleteResult}
              debouncedSearchQuery={debouncedSearchQuery}
              onEventPress={handleEventPress}
              onPerformerPress={handlePerformerPress}
              onVenuePress={handleVenuePress}
            />
          </Autocomplete>
        </View>
      </View>

      <View className="mt-6">
        <>
          {Object.keys(pastSearchResults).length > 1 && (
            <Pressable
              className="flex-row items-center mb-5"
              onPress={handleGoBack}
            >
              <Chevron direction="left" color="#3b82f6" />
              <Text style={{ color: "#3b82f6" }}>Back</Text>
            </Pressable>
          )}

          <Text category="h5">{getHeaderByParams}</Text>

          <Spacer />

          {cachedResult?.value && (
            <EventList
              events={(cachedResult?.value).content}
              onEventPress={(event) => handleEventPress(event)}
              onCategoryPress={(categoryName) =>
                handleCategoryPress(categoryName)
              }
              onVenuePress={(venue) => handleVenuePress(venue)}
              loading={searchingEvents}
              emptyMessage="No events match your search criteria"
            />
          )}
          {cachedResult?.value && (
            <Pagination
              currentPage={currentPage}
              totalPages={Math.min(cachedResult?.value.totalPages || 0, 10000)}
              onPageChange={handlePageChange}
            />
          )}
        </>
      </View>
    </View>
  );
};

export default TflEventSearch;
